import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { DateTime } from 'luxon';
import { finalize, take } from 'rxjs';
import { UserWarningModel, UserWarningSeverity } from 'app/models/user_warning.model';
import { UserWarningService } from 'app/services/user_warning.service';
import { ToastService } from 'app/shared/services/toast.service';
import { ApiService } from 'app/shared/apis/api.service';
import { UserModel } from 'app/models/user.model';
import { ModuleAttachmentsComponent } from 'app/shared/components/module-attachments/module-attachments.component';

export type UserWarningFormDialogData = {
    item?: UserWarningModel | null;
    user_id: number;
};

type WarningForm = FormGroup<{
    warning_date: FormControl<DateTime | Date | string | null>;
    reason: FormControl<string | null>;
    description: FormControl<string | null>;
    severity: FormControl<UserWarningSeverity | null>;
}>;

@Component({
    selector: 'app-user-warning-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatInputModule,
        MatIconModule,
        MatSelectModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        NgxMaskDirective,
        ModuleAttachmentsComponent,
    ],
    providers: [provideNgxMask()],
    templateUrl: './user-warning-form.component.html',
})
export class UserWarningFormComponent implements OnInit {
    @ViewChild(ModuleAttachmentsComponent) attachmentsCmp?: ModuleAttachmentsComponent;

    form: WarningForm = this.fb.group({
        warning_date: [null as DateTime | Date | string | null, Validators.required],
        reason: [null as string | null, Validators.required],
        description: [null as string | null],
        severity: ['LOW' as UserWarningSeverity, Validators.required],
    });

    loading = false;
    loggedUser: UserModel | null = null;
    currentItem: UserWarningModel | null = null;
    severityOptions: { value: UserWarningSeverity; label: string }[] = [
        { value: 'LOW', label: 'Leve' },
        { value: 'MEDIUM', label: 'Média' },
        { value: 'HIGH', label: 'Grave' },
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: UserWarningFormDialogData,
        private dialogRef: MatDialogRef<UserWarningFormComponent>,
        private fb: FormBuilder,
        private service: UserWarningService,
        private toast: ToastService,
    ) {
        this.dialogRef.addPanelClass('card-dialog');
    }

    ngOnInit(): void {
        ApiService.userCurrent.pipe(take(1)).subscribe((u) => (this.loggedUser = u));
        this.currentItem = this.data?.item ? new UserWarningModel(this.data.item) : null;
        if (this.currentItem?.user_warning_id) {
            this.form.patchValue({
                warning_date: this.toDateTime(this.currentItem.warning_date),
                reason: this.currentItem.reason,
                description: this.currentItem.description,
                severity: this.currentItem.severity,
            });
        }
    }

    get isEdit(): boolean {
        return !!this.currentItem?.user_warning_id;
    }

    get entityId(): number | null {
        return this.currentItem?.user_warning_id ?? null;
    }

    private toDateTime(v: Date | string | null | undefined): DateTime | null {
        if (!v) return null;
        if (DateTime.isDateTime(v)) return v as DateTime;
        if (v instanceof Date) return DateTime.fromJSDate(v);
        if (typeof v === 'string') {
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return DateTime.fromFormat(v, 'dd/MM/yyyy');
            const iso = DateTime.fromISO(v.slice(0, 10));
            return iso.isValid ? iso : null;
        }
        return null;
    }

    private toIsoDate(v: DateTime | Date | string | null): string | null {
        if (!v) return null;
        if (typeof v === 'string') {
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(v.trim())) {
                const dt = DateTime.fromFormat(v.trim(), 'dd/MM/yyyy');
                return dt.isValid ? dt.toISODate() : null;
            }
            if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
            return null;
        }
        if (DateTime.isDateTime(v)) return v.isValid ? v.toISODate() : null;
        if (v instanceof Date) return DateTime.fromJSDate(v).toISODate();
        return null;
    }

    clear(): void {
        this.dialogRef.close(false);
    }

    done(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const value = this.form.getRawValue();
        const warningIso = this.toIsoDate(value.warning_date);
        if (!warningIso) {
            this.toast.error('Data inválida.');
            return;
        }

        this.loading = true;
        const payload: any = {
            user_id: this.data.user_id,
            warning_date: warningIso,
            reason: value.reason,
            description: value.description,
            severity: value.severity,
            created_by_user_id: this.loggedUser?.user_id ?? null,
        };

        const wasEdit = this.isEdit;
        const req$ = wasEdit
            ? this.service.update({ ...this.currentItem, ...payload } as any)
            : this.service.create(payload);

        req$.pipe(take(1)).subscribe({
            next: async (saved) => {
                try {
                    const item = new UserWarningModel(saved as any);
                    this.currentItem = item;
                    const id = item.user_warning_id;
                    if (id && this.attachmentsCmp?.hasChanges) {
                        await this.attachmentsCmp.saveAttachments(id, { silent: true });
                    }
                    this.toast.success(wasEdit ? 'Advertência atualizada.' : 'Advertência registrada.');
                    this.dialogRef.close(true);
                } catch {
                    // Erro de anexo já toastado; mantém dialog aberto
                } finally {
                    this.loading = false;
                }
            },
            error: (err) => {
                this.loading = false;
                this.toast.error(err?.error?.message || 'Erro ao salvar.');
            },
        });
    }
}
